class DetailsModal extends HTMLElement {
  constructor() {
    super();
    
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.closeBlocked = false;
    this.closeBlockTimer = null;
    this.isModalOpen = false;
    this.savedScrollPosition = { x: 0, y: 0 };
    
    // Store active scroll lock handlers for cleanup
    this.activeScrollLocks = [];

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  clearAllScrollLocks() {
    // Remove all active scroll locks immediately
    this.activeScrollLocks.forEach(lockData => {
      if (lockData.scrollHandler) {
        window.removeEventListener('scroll', lockData.scrollHandler, { capture: true });
        document.removeEventListener('scroll', lockData.scrollHandler, { capture: true });
      }
      if (lockData.preventScroll) {
        window.removeEventListener('wheel', lockData.preventScroll, { capture: true });
        window.removeEventListener('touchmove', lockData.preventScroll, { capture: true });
      }
      if (lockData.interval) {
        clearInterval(lockData.interval);
      }
      if (lockData.timeout) {
        clearTimeout(lockData.timeout);
      }
    });
    
    this.activeScrollLocks = [];
  }

  preventScrollDuringTransition(scrollX, scrollY, duration = 150) {
    // Don't lock if modal is not open
    if (!this.isModalOpen) {
      return;
    }
    
    // Create a more aggressive scroll lock
    const scrollLockHandler = (e) => {
      if (!this.isModalOpen) return; // Extra safety check
      e.preventDefault();
      e.stopImmediatePropagation();
      window.scrollTo(scrollX, scrollY);
    };
    
    // Lock scroll on multiple events
    window.addEventListener('scroll', scrollLockHandler, { capture: true, passive: false });
    document.addEventListener('scroll', scrollLockHandler, { capture: true, passive: false });
    
    // Also prevent wheel and touch events that could cause scroll
    const preventScroll = (e) => {
      if (!this.isModalOpen) return; // Extra safety check
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    
    window.addEventListener('wheel', preventScroll, { capture: true, passive: false });
    window.addEventListener('touchmove', preventScroll, { capture: true, passive: false });
    
    // Force position multiple times during transition
    const maintainPosition = setInterval(() => {
      if (!this.isModalOpen) {
        clearInterval(maintainPosition);
        return;
      }
      window.scrollTo(scrollX, scrollY);
    }, 10);
    
    // Remove all locks after duration
    const timeoutId = setTimeout(() => {
      window.removeEventListener('scroll', scrollLockHandler, { capture: true });
      document.removeEventListener('scroll', scrollLockHandler, { capture: true });
      window.removeEventListener('wheel', preventScroll, { capture: true });
      window.removeEventListener('touchmove', preventScroll, { capture: true });
      clearInterval(maintainPosition);
      
      // Remove from active locks
      const index = this.activeScrollLocks.findIndex(lock => lock.timeout === timeoutId);
      if (index > -1) {
        this.activeScrollLocks.splice(index, 1);
      }
      
      // Final position enforcement - only if modal is still open
      if (this.isModalOpen) {
        window.scrollTo(scrollX, scrollY);
      }
    }, duration);
    
    // Store lock data for potential cleanup
    this.activeScrollLocks.push({
      scrollHandler: scrollLockHandler,
      preventScroll: preventScroll,
      interval: maintainPosition,
      timeout: timeoutId
    });
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    
    // Save current scroll position
    this.savedScrollPosition.x = window.scrollX;
    this.savedScrollPosition.y = window.scrollY;
    
    // Mark modal as open
    this.isModalOpen = true;
    
    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('modal:opening', { detail: { modalType: 'search' } }));
    
    // Lock scroll position during transition
    this.preventScrollDuringTransition(this.savedScrollPosition.x, this.savedScrollPosition.y, 200);
    
    document.body.addEventListener('click', this.onBodyClickEvent);
    
    // Add overflow-hidden class
    document.body.classList.add('overflow-hidden');
    
    // Force scroll position immediately
    window.scrollTo(this.savedScrollPosition.x, this.savedScrollPosition.y);

    /* ---- Block close for 500 ms ---- */
    this.closeBlocked = true;
    clearTimeout(this.closeBlockTimer);
    this.closeBlockTimer = setTimeout(() => {
      this.closeBlocked = false;
    }, 500);

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    // CRITICAL: Check if modal is actually open before attempting to close
    if (!this.isModalOpen || !this.detailsContainer.hasAttribute('open')) {
      return;
    }

    if (this.closeBlocked) {
      return;
    }

    // Mark modal as closed FIRST to stop all scroll locks
    this.isModalOpen = false;
    
    // CRITICAL: Clear all scroll locks immediately
    this.clearAllScrollLocks();
    
    // Dispatch event
    window.dispatchEvent(new CustomEvent('modal:closing', { detail: { modalType: 'search' } }));

    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    
    // Remove overflow-hidden
    document.body.classList.remove('overflow-hidden');
    
    // Restore to the position where modal was opened (ONE TIME ONLY)
    window.scrollTo(this.savedScrollPosition.x, this.savedScrollPosition.y);
    
    // NO MORE scroll locking after close
    // Just one final position restore without any locks
    requestAnimationFrame(() => {
      window.scrollTo(this.savedScrollPosition.x, this.savedScrollPosition.y);
    });
  }
}

customElements.define('details-modal', DetailsModal);